import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscriptionRequired } from '@/hooks/useSubscriptionRequired';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { track } from '@/lib/analytics';
import { AppLayout } from '@/components/layout/AppLayout';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ImageCropModal } from '@/components/ui/image-crop-modal';
import { MapPicker } from '@/components/ui/map-picker';
import { Camera, User, MapPin, FileText, Calendar as CalendarIcon, HeartPulse, Lock, ShieldCheck } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format, parse, isValid } from 'date-fns';
import { Geolocation } from '@capacitor/geolocation';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LegalAcceptanceCheckbox } from '@/components/legal/LegalAcceptanceCheckbox';
import { LegalDocDialog } from '@/components/legal/LegalDocDialog';
import { compressImage } from '@/lib/image-utils';
import { createDefaultCareSetup } from '@/lib/default-care-setup';
import { Checkbox } from '@/components/ui/checkbox';

export default function NewPatientPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, subscription } = useAuth();
  const isSubscribed = subscription.subscribed;
  const { canCreatePatient, patientsCount, patientsLimit, isOwner, isLoading: isCheckingLimit } = useSubscriptionRequired();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [nickname, setNickname] = useState('');
  const [birthDate, setBirthDate] = useState(''); // ISO yyyy-MM-dd
  const [birthDateInput, setBirthDateInput] = useState(''); // typed display
  const [address, setAddress] = useState('');
  const [healthDetails, setHealthDetails] = useState('');
  const [notes, setNotes] = useState('');
  const autoLocateAttemptedRef = useRef(false);

  // Location states
  const [residenceLatitude, setResidenceLatitude] = useState<number | null>(null);
  const [residenceLongitude, setResidenceLongitude] = useState<number | null>(null);

  // Photo crop state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [croppedPhoto, setCroppedPhoto] = useState<Blob | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  // Patient consent (LGPD)
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [createDefaults, setCreateDefaults] = useState(true);
  const [consentDocOpen, setConsentDocOpen] = useState(false);
  const [grantorRole, setGrantorRole] = useState<'self' | 'legal_guardian' | 'attorney_in_fact'>('legal_guardian');
  const [grantorName, setGrantorName] = useState('');

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('profile.selectImage'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // Relaxed since we compress
      toast.error(t('profile.imageMaxSize'));
      return;
    }

    setSelectedFile(file);
    setCropModalOpen(true);
    e.target.value = '';
  };

  useEffect(() => {
    return () => {
      if (photoPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  // Auto-detect device location on first load (silently — no toast on failure)
  useEffect(() => {
    if (autoLocateAttemptedRef.current) return;
    autoLocateAttemptedRef.current = true;

    let cancelled = false;
    const setDetectedLocation = (latitude: number, longitude: number) => {
      if (cancelled) return;
      setResidenceLatitude(latitude);
      setResidenceLongitude(longitude);
    };

    const tryBrowser = () => {
      if (!('geolocation' in navigator)) return;
      navigator.geolocation.getCurrentPosition(
        (position) => setDetectedLocation(position.coords.latitude, position.coords.longitude),
        () => { /* Keep the map available for manual selection. */ },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    };

    const tryCapacitor = async () => {
      try {
        const status = await Geolocation.checkPermissions();
        if (status.location !== 'granted') {
          const req = await Geolocation.requestPermissions();
          if (req.location !== 'granted') {
            tryBrowser();
            return;
          }
        }
        const coords = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        });
        setDetectedLocation(coords.coords.latitude, coords.coords.longitude);
      } catch {
        tryBrowser();
      }
    };
    tryCapacitor();
    return () => { cancelled = true; };
  }, []);

  const uploadPhoto = async (patientId: string): Promise<string | null> => {
    if (!croppedPhoto) return null;

    const filePath = `${patientId}/photo.jpg`;

    const { error: uploadError } = await supabase.storage
      .from('patient-photos')
      .upload(filePath, croppedPhoto, { upsert: true, contentType: 'image/jpeg' });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      return null;
    }

    // Store the file path reference - signed URLs will be generated when viewing
    const { data } = supabase.storage
      .from('patient-photos')
      .getPublicUrl(filePath);

    return `${data.publicUrl}?t=${Date.now()}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!nickname.trim()) {
      toast.error(t('common.required'));
      return;
    }

    if (!consentAccepted) {
      toast.error(t('patient.consent.mustAccept') || 'É necessário aceitar o Termo de Consentimento do Paciente');
      return;
    }

    if (grantorRole !== 'self' && !grantorName.trim()) {
      toast.error(t('patient.consent.nameRequired') || 'Informe o nome completo de quem está autorizando o uso dos dados');
      return;
    }

    // Check patient limit (works for both free and subscribed users)
    if (isOwner && !canCreatePatient) {
      toast.error(isSubscribed
        ? t('subscription.patientLimit.blockedSubscribed')
        : t('subscription.patientLimit.blockedFree'));
      navigate('/app/subscription');
      return;
    }

    if (!user) {
      toast.error(t('common.error'));
      return;
    }

    setIsLoading(true);

    try {
      const { data: patient, error: patientError } = await supabase
        .from('patients')
        .insert({
          nickname: nickname.trim(),
          birth_date: birthDate || null,
          address: address.trim() || null,
          health_details: healthDetails.trim() || null,
          notes: notes.trim() || null,
          residence_latitude: residenceLatitude,
          residence_longitude: residenceLongitude,
          created_by: user.id
        })
        .select()
        .single();

      if (patientError) throw patientError;

      if (croppedPhoto && patient) {
        const photoUrl = await uploadPhoto(patient.id);
        if (photoUrl) {
          await supabase
            .from('patients')
            .update({ photo_url: photoUrl })
            .eq('id', patient.id);
        }
      }

      const { error: linkError } = await supabase
        .from('patient_user_links')
        .insert({
          patient_id: patient.id,
          user_id: user.id,
          role: 'owner'
        });

      if (linkError) throw linkError;

      // Owner role is auto-granted by DB trigger on patients insert

      // Register LGPD patient consent (non-blocking for UX on failure,
      // but we warn the user so they can retry from the privacy center later)
      try {
        const { error: consentError } = await supabase.functions.invoke('record-patient-consent', {
          body: {
            patient_id: patient.id,
            granted_by_role: grantorRole,
            granted_by_name: grantorRole === 'self' ? null : grantorName.trim() || null,
          },
        });
        if (consentError) throw consentError;
      } catch (err) {
        console.warn('[NewPatient] failed to record patient consent', err);
        toast.warning(t('patient.consent.recordFailed') || 'Paciente criado, mas o registro do consentimento falhou. Registre novamente em Privacidade.');
      }

      let defaultsCreated = false;
      if (createDefaults) {
        try {
          await createDefaultCareSetup(patient.id, user.id, t);
          defaultsCreated = true;
        } catch (err) {
          console.warn('[NewPatient] default care setup failed', err);
          toast.warning(t('defaultCare.failed'));
        }
      }

      if (defaultsCreated) {
        const pid = patient.id;
        toast.success(t('defaultCare.created'), {
          action: { label: t('defaultCare.inviteCaregiver'), onClick: () => navigate(`/app/patients/${pid}`) },
        });
      } else {
        toast.success(t('common.success'));
      }

      // `patient_created` is emitted server-side (trigger on public.patients)
      // so it is captured regardless of cookie consent / app version.

      await queryClient.invalidateQueries({ queryKey: ['subscription-required'] });
      await queryClient.invalidateQueries({ queryKey: ['patients-list'] });
      navigate('/app/patients');

    } catch (error: any) {
      console.error('Error creating patient:', error);
      const msg = String(error?.message || '');
      if (msg.includes('family_patient_limit_exceeded') || msg.includes('company_patient_limit_exceeded')) {
        toast.error(isSubscribed
          ? t('subscription.patientLimit.blockedSubscribed')
          : t('subscription.patientLimit.blockedFree'));
        await queryClient.invalidateQueries({ queryKey: ['subscription-required'] });
        navigate('/app/subscription');
        return;
      }
      toast.error(error.message || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AppLayout showFAB={false} title={t('patient.addPatient')} showBack>

      {/* Patient limit warning */}
      {isOwner && !canCreatePatient && !isCheckingLimit && (
        <Alert variant="destructive" className="mb-6">
          <Lock className="h-4 w-4" />
          <AlertDescription className="flex flex-col gap-2">
            <span>
              {isSubscribed
                ? t('subscription.patientLimit.warningSubscribed', { limit: patientsLimit, count: patientsCount })
                : t('subscription.patientLimit.warningFree', { limit: patientsLimit, count: patientsCount })}
            </span>
            <Button size="sm" variant="outline" onClick={() => navigate('/app/subscription')}>
              {isSubscribed ? t('subscription.manageSubscription') : t('subscription.subscribe')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card className="mb-6">
          <CardContent className="pt-6">
            {/* Photo Upload */}
            <div className="flex flex-col items-center mb-6">
              <div
                className="relative cursor-pointer group"
                onClick={() => fileInputRef.current?.click()}
              >
                <Avatar className="w-24 h-24">
                  <AvatarImage src={photoPreview || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    <User className="w-10 h-10" />
                  </AvatarFallback>
                </Avatar>
                <div className="absolute inset-0 flex items-center justify-center bg-foreground/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="w-8 h-8 text-background" />
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoChange}
              />
              <p className="text-sm text-muted-foreground mt-2">
                {photoPreview ? t('patient.tapToChangePhoto') : t('patient.tapToAddPhoto')}
              </p>
            </div>

            <ImageCropModal
              open={cropModalOpen}
              imageFile={selectedFile}
              onClose={() => {
                setCropModalOpen(false);
                setSelectedFile(null);
              }}
              onCrop={async (blob) => {
                setCropModalOpen(false);
                setSelectedFile(null);

                // Compress cropped photo
                const { blob: finalBlob, previewUrl } = await compressImage(blob, {
                  maxWidth: 512,
                  maxHeight: 512,
                  quality: 0.8
                });

                setCroppedPhoto(finalBlob);
                if (photoPreview?.startsWith('blob:')) {
                  URL.revokeObjectURL(photoPreview);
                }
                setPhotoPreview(previewUrl);
              }}
              size={256}
            />

            {/* Patient name */}
            <div className="space-y-2 mb-4">
              <Label htmlFor="nickname" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {t('patient.patientName')} *
              </Label>
              <Input
                id="nickname"
                placeholder={t('patient.patientNamePlaceholder')}
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                required
              />
            </div>

            {/* Birth Date */}
            <div className="space-y-2 mb-4">
              <Label htmlFor="birthDate" className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                {t('patient.birthDate')}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="birthDate"
                  inputMode="numeric"
                  placeholder={t('patient.birthDatePlaceholder')}
                  value={birthDateInput}
                  onChange={(e) => {
                    const raw = e.target.value;
                    // Auto-mask digits as dd/MM/yyyy
                    const digits = raw.replace(/\D/g, '').slice(0, 8);
                    let masked = digits;
                    if (digits.length > 4) masked = `${digits.slice(0,2)}/${digits.slice(2,4)}/${digits.slice(4)}`;
                    else if (digits.length > 2) masked = `${digits.slice(0,2)}/${digits.slice(2)}`;
                    setBirthDateInput(masked);
                    if (digits.length === 8) {
                      const parsed = parse(masked, 'dd/MM/yyyy', new Date());
                      if (isValid(parsed) && parsed <= new Date() && parsed >= new Date('1900-01-01')) {
                        setBirthDate(format(parsed, 'yyyy-MM-dd'));
                      } else {
                        setBirthDate('');
                      }
                    } else {
                      setBirthDate('');
                    }
                  }}
                  className="flex-1"
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" size="icon" aria-label={t('patient.birthDate')}>
                      <CalendarIcon className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="single"
                      selected={birthDate ? parse(birthDate, 'yyyy-MM-dd', new Date()) : undefined}
                      onSelect={(d) => {
                        if (d) {
                          setBirthDate(format(d, 'yyyy-MM-dd'));
                          setBirthDateInput(format(d, 'dd/MM/yyyy'));
                        }
                      }}
                      disabled={(date) => date > new Date() || date < new Date('1900-01-01')}
                      initialFocus
                      className={cn('p-3 pointer-events-auto')}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {birthDateInput.length === 10 && !birthDate && (
                <p className="text-xs text-destructive">{t('patient.birthDateInvalid')}</p>
              )}
            </div>

            {/* Address */}
            <div className="space-y-2 mb-4">
              <Label htmlFor="address" className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {t('patient.address')}
              </Label>
              <Input
                id="address"
                placeholder={t('patient.address')}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            {/* Health Details */}
            <div className="space-y-2 mb-4">
              <Label htmlFor="healthDetails" className="flex items-center gap-2">
                <HeartPulse className="w-4 h-4" />
                {t('patient.healthDetails')}
              </Label>
              <Textarea
                id="healthDetails"
                placeholder={t('patient.healthDetails')}
                value={healthDetails}
                onChange={(e) => setHealthDetails(e.target.value)}
                rows={4}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2 mb-4">
              <Label htmlFor="notes" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {t('patient.observations')}
              </Label>
              <Textarea
                id="notes"
                placeholder={t('patient.observations')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>

            {/* Residence Location */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center justify-between mb-4">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {t('patient.residenceLocation')}
                </Label>
              </div>

              <div className="space-y-2">
                <MapPicker
                  latitude={residenceLatitude}
                  longitude={residenceLongitude}
                  onLocationSelect={(lat, lng) => {
                    setResidenceLatitude(lat);
                    setResidenceLongitude(lng);
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  {residenceLatitude !== null && residenceLongitude !== null
                    ? t('patient.locationDetectedAuto')
                    : t('patient.locationHelpText')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* LGPD — Patient Consent */}
        <Card className="mb-6 border-primary/30">
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <h3 className="font-semibold text-sm">
                  {t('patient.consent.title') || 'Consentimento do Paciente (LGPD)'}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('patient.consent.description') ||
                    'Para usar e proteger os dados de saúde deste paciente, precisamos da autorização do próprio paciente ou de alguém que possa representá-lo legalmente.'}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="grantor-role" className="text-sm">
                {t('patient.consent.grantorRoleLabel') || 'Quem autoriza o uso dos dados de saúde do paciente?'}
              </Label>
              <Select value={grantorRole} onValueChange={(v) => setGrantorRole(v as typeof grantorRole)}>
                <SelectTrigger id="grantor-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">{t('patient.consent.grantor.self') || 'O próprio paciente'}</SelectItem>
                  <SelectItem value="legal_guardian">{t('patient.consent.grantor.legalGuardian') || 'Responsável legal pelo paciente'}</SelectItem>
                  <SelectItem value="attorney_in_fact">{t('patient.consent.grantor.attorneyInFact') || 'Procurador autorizado'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {grantorRole !== 'self' && (
              <div className="space-y-2">
                <Label htmlFor="grantor-name" className="text-sm">
                  {t('patient.consent.grantorNameLabel') || 'Nome completo de quem está autorizando'}
                </Label>
                <Input
                  id="grantor-name"
                  value={grantorName}
                  onChange={(e) => setGrantorName(e.target.value)}
                  placeholder={t('patient.consent.grantorNamePlaceholder') || 'Nome completo'}
                />
                <p className="text-xs text-muted-foreground">
                  {t('patient.consent.grantorNameHelp') || 'Informe o nome do responsável legal ou procurador selecionado acima.'}
                </p>
              </div>
            )}

            <LegalAcceptanceCheckbox
              id="patient-consent-checkbox"
              checked={consentAccepted}
              onCheckedChange={setConsentAccepted}
            >
              {t('patient.consent.acceptPrefix') || 'Li e concordo com o'}{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setConsentDocOpen(true);
                }}
                className="text-primary underline underline-offset-2 hover:text-primary/80"
              >
                {t('patient.consent.docLabel') || 'Termo de Consentimento do Paciente'}
              </button>
              {', '}
              {t('patient.consent.acceptSuffix') ||
                'autorizando o tratamento dos dados pessoais e de saúde do paciente para as finalidades descritas.'}
            </LegalAcceptanceCheckbox>
            <LegalDocDialog
              slug="patient-consent"
              fallbackTitle={t('patient.consent.docLabel') || 'Termo de Consentimento do Paciente'}
              open={consentDocOpen}
              onOpenChange={setConsentDocOpen}
              onAccept={() => setConsentAccepted(true)}
            />
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Checkbox id="create-defaults" checked={createDefaults} onCheckedChange={(v) => setCreateDefaults(v === true)} className="mt-0.5" />
              <div className="space-y-1">
                <Label htmlFor="create-defaults" className="cursor-pointer">{t('defaultCare.optionLabel')}</Label>
                <p className="text-sm text-muted-foreground">{t('defaultCare.optionHint')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Button 
          type="submit" 
          className="w-full" 
          disabled={isLoading || (isOwner && !canCreatePatient) || !consentAccepted}
        >
          {isLoading ? t('common.saving') : t('common.save')}
        </Button>
      </form>
    </AppLayout>
  );
}
